/* ═══════════════════════════════════════════════════════════════
   TAO Clean homepage prototype — controller

   Screens/state:
     single page; global state = active colorway theme
     - state.themeId   persisted in localStorage('tao-theme')
     - state.playing   auto-cycle on/off (not persisted)
   Interactions:
     - hero picker swatches (below Shop now) / ← → keys change theme
     - ▶ button cycles every 3.2s
     - nav CTAs smooth-scroll to sections
     - hero render plate crossfades between colorways
   ═══════════════════════════════════════════════════════════════ */
import { THEMES as THEME_DEFINITIONS, DEFAULT_THEME, heroExp } from './tao-mockup-themes.js?v=19colors2';
import { HERO_DESKTOP_VIDEO, HERO_MOBILE_VIDEO } from './tao-mockup-video-urls.js?v=3';
import { createUmmaHero } from './umma-hero.js?v=initial-fade-20260915a';

const $ = (s) => document.querySelector(s);
const root = document.documentElement;
const productHero = document.querySelector('[data-tao-mockup-page="product"]');
const heroController = productHero ? createUmmaHero(productHero) : null;
/* A mockup PDP that brings its own per-colourway hero art (the water flosser) marks
   its hero data-tao-hero-art="variant". Every colourway keeps its palette, but it is
   drawn as catalog art: the two art layers show the variant JSON's heroImage (and
   heroImageSmall through #art-*-src on phones), and none of the toothbrush plates,
   cut-outs or films are fetched or warmed. Absent on every other page. */
const USES_VARIANT_ART = productHero?.dataset.taoHeroArt === 'variant';
const REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');
if (heroController) root.classList.add('hero-ready');
else root.classList.remove('hero-ready');

/* Preserve Liquid's Shopify variant order while retaining the visual palettes. */
let shopifyVariants = [];
try {
    shopifyVariants = JSON.parse(
        document.querySelector('[data-tao-mockup-variants]')?.textContent || '[]'
    );
} catch (error) {
    console.warn('TAO mockup variant data could not be parsed.', error);
}
const liveThemeMap = new Map(shopifyVariants.map(variant => [
    variant.themeId || `variant-${variant.id}`, variant,
]));
const themeOrder = [...liveThemeMap.keys()];
const THEMES = shopifyVariants.length ? themeOrder.map(id => {
    const variant = liveThemeMap.get(id);
    const definition = THEME_DEFINITIONS.find(theme => theme.id === id);
    return {
        ...(definition || THEME_DEFINITIONS.find(theme => theme.id === DEFAULT_THEME)),
        id,
        name: variant.title || variant.options?.[0] || definition?.name || id,
        tier: definition?.tier,
        price: variant.price / 100,
        available: variant.available,
        variantId: variant.id,
        catalogImage: USES_VARIANT_ART ? (variant.heroImage || variant.image) : variant.image,
        catalogImageSmall: USES_VARIANT_ART ? variant.heroImageSmall : undefined,
        catalogFallback: !definition || USES_VARIANT_ART,
        ...(!definition || USES_VARIANT_ART ? { staticHero: true } : {}),
    };
}) : THEME_DEFINITIONS;

const els = {
  layers: [
    /* pic = the ENTITY (plate + product cutout fade as one unit) */
    { pic: $('#art-layer-a'), src: $('#art-a-src'), avif: $('#art-a-avif'), webp: $('#art-a-webp'), img: $('#art-a-img'), brush: $('#art-a-brush'), leaf: $('#art-a-leaf') },
    { pic: $('#art-layer-b'), src: $('#art-b-src'), avif: $('#art-b-avif'), webp: $('#art-b-webp'), img: $('#art-b-img'), brush: $('#art-b-brush'), leaf: $('#art-b-leaf') },
  ],
  swatches: $('#swatches'),
  name: $('#picker-name'),
  play: $('#play'),
};

/* Shopify's exact variant URL is authoritative. ?color= remains as a legacy
   deep-link fallback, while Liquid provides the selected variant's visual ID
   before this module runs so first paint and interactive state cannot disagree. */
const _params = new URLSearchParams(location.search);
const _qVariant = _params.get('variant');
let _variantTheme = null;
const requestedVariant = shopifyVariants.find(variant => String(variant.id) === _qVariant);
if (requestedVariant) _variantTheme = requestedVariant.themeId || `variant-${requestedVariant.id}`;
const _qColor = _params.get('color');
if (_qColor && THEMES.some(t => t.id === _qColor)) {
  try { localStorage.setItem('tao-theme', _qColor); } catch {}
}

const state = {
  themeId: _variantTheme
    || (_qColor && THEMES.some(t => t.id === _qColor) ? _qColor : null)
    || window.__bootTheme
    || DEFAULT_THEME,
  playing: false,
  front: 0,              // which art layer is visible
  timer: null,
  waveTimer: null,
  waveOutMs: 0,          // the rings' fade-out, read once from the stylesheet
  requestedThemeId: null,
  themeGen: 0,
};
if (!THEMES.some(t => t.id === state.themeId)) state.themeId = THEMES[0]?.id;
state.requestedThemeId = state.themeId;

/* the display plate is the ORIGINAL render (the videos animate it), so
   warm THAT, not the /exp/ variant the swatch was built from */
const heroExpOrig = (t) => heroExp(t).replace('--exp--', '--exp-orig--');
const HERO_DESKTOP = matchMedia('(min-width: 921px)');

/* ── the phone's hero is the DESKTOP hero, cropped ─────────────────
   It used to be assets/hero/m/ — thirteen separately generated scenes, one per
   colourway, and they were thirteen different photographs: different counters
   (marble, wood, travertine), different backgrounds, different product scale,
   different lighting, the pair sitting at a different height in each. A hand-measured
   nudge table lived here to drag every brush tip back to Midnight's height, which is
   the tell — the scenes never matched, and the table was the patch.

   assets/hero/p/ is a 1024-wide window cut out of the SAME 3000x1536 plate the desktop
   serves, at the same offset for every colourway, which is possible because every
   theme's hero.cx is 0.555 — the plates are framed identically, which is exactly why
   desktop never had this problem. One window, one framing, thirteen colours. No table.

   The window is the full height and centred on cx, which lands the pair in the upper
   half and leaves the dark floor for the copy — the structure the generated scenes were
   reaching for, arrived at by cropping the real thing instead. */
const MHERO_VER = 1;
/* assetPlates colorways ship their plates as theme assets rather than Files
   renders; the assets directory is wherever this module was served from.
   PLATE_VER busts the CDN on plate updates — these URLs are built here rather
   than through asset_url, so without it an edge keeps serving the old pixels
   (bump the boot script in tao-mockup-pdp-1.liquid in step). */
const PLATE_VER = 4;
const ASSET_BASE = new URL('.', import.meta.url).href;
const heroMobile = (t) => (t.assetPlates
  ? `${ASSET_BASE}tao-mockup-hero-p-${t.id}.webp?v=${PLATE_VER}`
  : `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--hero--p--${t.id}.webp?v=${MHERO_VER}`);

/* prime a colorway's hero video into the HTTP cache so it fades in
   promptly after a switch (the switch itself no longer waits on it).
   Kept referenced so the buffer isn't dropped. Hidden on mobile. */
const vwarm = new Map();
function preloadVideo(id) {
  if (heroController || REDUCED_MOTION.matches || !HERO_DESKTOP.matches) return;
  const url = leafSrc(id);
  if (!url || vwarm.has(url)) return;   /* static colorways have no clip */
  const v = document.createElement('video');
  v.muted = true; v.playsInline = true; v.preload = 'auto'; v.src = url;
  v.load();
  vwarm.set(url, v);
}

/* ── which image format this browser will actually take ────────────────
   The <picture> in the markup answers this for the plate the page is SHOWING, because
   that is what <picture> is for. Warming is different: it is `new Image()` with one URL,
   and picking the PNG there would download three and a half megabytes to warm a plate
   the page will then fetch as a 48KB AVIF — warming the wrong file and paying twice.

   Decided once, by decoding a one-pixel image of each format. Everything falls back
   through the same ladder the <picture> uses, so the two can never disagree. */
const AVIF_1PX = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAABEaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=';
const WEBP_1PX = 'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==';
let plateFmt = 'png';
const fmtReady = (async () => {
  if (heroController) return;
  const can = src => new Promise(r => { const i = new Image(); i.onload = () => r(i.width > 0); i.onerror = () => r(false); i.src = src; });
  if (await can(AVIF_1PX)) plateFmt = 'avif';
  else if (await can(WEBP_1PX)) plateFmt = 'webp';
})();
const plateURL = (t) => (t.assetPlates
  ? (plateFmt === 'png'
    ? `${ASSET_BASE}tao-mockup-hero-exp-orig-${t.id}.png?v=${PLATE_VER}`
    : `${ASSET_BASE}tao-mockup-hero-exp-${plateFmt}-${t.id}.${plateFmt}?v=${PLATE_VER}`)
  : plateFmt === 'png'
    ? heroExpOrig(t)
    : `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--hero--exp-${plateFmt}--${t.id}.${plateFmt}?v=1`);
const brushURL = (id) => (plateFmt === 'png'
  ? `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--hero--brush-${id}.png`
  : `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--hero--brush-${plateFmt}--${id}.${plateFmt}?v=1`);

/* warm neighbour plates so cycling never flashes or lags */
const preloaded = new Set();
/* Has anyone actually gone looking at colours yet? Until they have, the only film worth
   downloading is the one on screen — most visitors never touch the picker, and warming
   two neighbours costs four megabytes to make an arrow press instant for the ones who
   do. After the first switch the intent is obvious and the warming earns its keep. */
let browsingColours = false;
function preload(t) {
  if (heroController || !t || t.catalogFallback) return;
  /* The neighbour's CLIP is warmed on idle, and only once someone is browsing. It is
     megabytes where the plate is tens of kilobytes, and the two were being asked for
     together on the critical path. */
  if (browsingColours) idle(() => preloadVideo(t.id));
  fmtReady.then(() => {
  const url = HERO_DESKTOP.matches ? plateURL(t) : heroMobile(t);
  if (preloaded.has(url)) return;
  new Image().src = url;
  /* The cutout is a desktop compositing layer. Loading it on phones was
     spending another 3.6 MB even though CSS never displays it there. */
  if (HERO_DESKTOP.matches && BRUSH_IDS.has(t.id)) new Image().src = brushURL(t.id);
  preloaded.add(url);
  });
}
const idle = (fn) => (window.requestIdleCallback
  ? requestIdleCallback(fn, { timeout: 4000 })
  : setTimeout(fn, 1200));

/* after load, quietly warm and PRE-DECODE every plate + cutout, one
   colorway at a time — any later pick then starts its fade within a
   frame or two instead of pausing on a download/decode */
const warmRefs = [];
async function warmAllPlates() {
  if (heroController || USES_VARIANT_ART) return;
  /* WAIT FOR THE FORMAT. plateFmt is decided by decoding a one-pixel image, which is
     asynchronous, and warming that starts first falls back to 'png' — which is how a
     measurement taken right after this change still showed three-and-a-half megabyte
     plates being fetched to warm plates the page had already taken as 48KB AVIFs. */
  await fmtReady;
  const mob = !HERO_DESKTOP.matches;
  /* NEVER ON A PHONE. This was about bandwidth — thirteen portrait crops is 860KB — and
     the bandwidth was the small half of the problem. A decoded image costs
     width x height x 4 bytes of BITMAP however small the file was, so each 1024x1536
     plate is 6.3MB in memory and thirteen of them is EIGHTY-TWO MEGABYTES, held alive
     deliberately by warmRefs so the decodes persist. On a desktop that is fine. On an
     iPhone, where Safari kills a tab a long way under a third of a gigabyte, it is most
     of the budget spent on twelve colourways nobody asked for — and it is why the page
     was crashing on scroll after a colour had been tapped.
     The two immediate neighbours still come from preload(), which is what a swatch tap
     actually needs. */
  if (mob) return;
  let i = 0;
  const step = () => {
    if (i >= THEMES.length) return;
    const t = THEMES[i++];
    const im = new Image();
    im.src = mob ? heroMobile(t) : plateURL(t);
    const br = (mob || !BRUSH_IDS.has(t.id)) ? null : new Image();
    if (br) br.src = brushURL(t.id);
    warmRefs.push(im, ...(br ? [br] : [])); /* keep refs so caches persist */
    /* NO VIDEO HERE. This line used to warm every colourway's clip, and it is what made
       the desktop PDP pull 97MB on a first load — 44MB of it video, thirteen four-to-ten
       megabyte films for twelve colourways nobody had asked for yet. The intent was
       right and the unit was wrong: a plate is 48KB and warming all thirteen costs
       640KB, which buys an instant switch for the price of one photograph. A film is
       megabytes. The two immediate neighbours get theirs from preload(), on idle, which
       is what an arrow press actually needs. */
    Promise.allSettled([
      im.decode ? im.decode().catch(() => {}) : null,
      br?.decode ? br.decode().catch(() => {}) : null,
    ]).then(() => setTimeout(step, 220));  /* stagger: never fight the page */
  };
  step();
}
addEventListener('load', () => {
  if (heroController || navigator.connection?.saveData) return;
  setTimeout(warmAllPlates, 1200);
});

/* comparison mode (canopy lab): serve the pre-cleanup leafy plates */
let useOrigPlates = false;
addEventListener('tao:plates', (e) => {
  const orig = !!e.detail?.orig;
  if (orig === useOrigPlates) return;
  useOrigPlates = orig;
  applyTheme(state.themeId, { instant: true });
});

const BRUSH_IDS = new Set(THEMES.filter(t => !t.staticHero).map(t => t.id));

function setLayer(layer, t, alt) {
  if (heroController || !layer?.img) return;
  if (t.catalogFallback) {
      layer.src.srcset = t.catalogImageSmall || '';
      if (layer.avif) layer.avif.srcset = '';
      if (layer.webp) layer.webp.srcset = '';
      layer.img.src = t.catalogImage || '';
      layer.img.alt = alt;
      layer.pic.dataset.theme = t.id;
      layer.brush?.removeAttribute('src');
      if (layer.leaf) {
          layer.leaf.pause();
          layer.leaf.removeAttribute('src');
          layer.leaf.style.opacity = '0';
      }
      return;
  }
  layer.src.srcset = heroMobile(t);
  /* The plate in the formats a browser should actually be given. It is a photographic
     render and it was shipping as a 3.4MB PNG — lossless, for a photograph — which made
     it by far the largest thing either page downloads. 43MB across the thirteen becomes
     642KB, and the <picture> keeps the PNG underneath for anything that can read neither
     format. Measured at RMSE 0.0035 against the source, and checked at 2.6x zoom on the
     darkest gradient in the frame, which is where an encoder shows its seams. */
  if (layer.avif) layer.avif.srcset = t.assetPlates
    ? `${ASSET_BASE}tao-mockup-hero-exp-avif-${t.id}.avif?v=${PLATE_VER}`
    : `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--hero--exp-avif--${t.id}.avif?v=1`;
  if (layer.webp) layer.webp.srcset = t.assetPlates
    ? `${ASSET_BASE}tao-mockup-hero-exp-webp-${t.id}.webp?v=${PLATE_VER}`
    : `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--hero--exp-webp--${t.id}.webp?v=1`;
  layer.pic.dataset.theme = t.id;
  /* every colorway's hero video animates its ORIGINAL render, so the
     still underneath must always match (useOrigPlates kept for the lab) */
  layer.img.src = t.assetPlates
    ? `${ASSET_BASE}tao-mockup-hero-exp-orig-${t.id}.png?v=${PLATE_VER}`
    : heroExpOrig(t);
  layer.img.alt = alt;
  if (layer.brush) {
    if (HERO_DESKTOP.matches && BRUSH_IDS.has(t.id)) {
      /* AVIF with a PNG fallback on error, rather than waiting for the format probe:
         this one is on the critical path — it composites with the plate and a late src
         is a visible flash — and 278KB of lossless cutout was being fetched every time
         because the probe had not resolved yet when the first theme applied. */
      layer.brush.onerror = () => { layer.brush.onerror = null; layer.brush.src = `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--hero--brush-${t.id}.png`; };
      layer.brush.src = `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--hero--brush-avif--${t.id}.avif?v=1`;
    }
    else layer.brush.removeAttribute('src');
  }
  setLeaf(layer, t);
}

/* ---------- offscreen-glass guard --------------------------------
   Chromium sometimes drops the painted content of backdrop-filter
   elements when the theme changes while they are scrolled offscreen
   (stale composited texture). Track hero visibility so plate swaps can
   skip animation offscreen, and force a repaint of each glass card the
   next time it scrolls back into view after an offscreen theme change. */
let heroInView = true;
const heroEl = document.querySelector('.hero');
if (heroEl) {
  new IntersectionObserver((entries) => {
    heroInView = entries.some(e => e.isIntersecting);
    els.layers.forEach(layoutLeaf);
    leafPlayback();
  }, { threshold: 0 }).observe(heroEl);
}

let glassEls = [];
const glassSeen = new WeakMap();   // el -> currently in view?
const glassIO = new IntersectionObserver((entries) => {
  for (const en of entries) {
    glassSeen.set(en.target, en.isIntersecting);
    if (!en.isIntersecting || en.target.dataset.repaint !== '1') continue;
    delete en.target.dataset.repaint;
    /* The new PDP's offscreen backdrop guard repaints the glass without
       removing the element from layout or restarting its reveal animation. */
    if (productHero) continue;
    if (en.target.contains(document.activeElement)) continue;  // keep focus alive
    en.target.style.display = 'none';
    void en.target.offsetHeight;
    en.target.style.display = '';
  }
}, { threshold: 0 });
/* EVERY backdrop-filter surface is vulnerable to the stale-texture bug
   (the lab card rotted next after the hardcoded four were guarded) —
   scan the built page once and protect them all */
addEventListener('load', () => setTimeout(() => {
  /* The new PDP repaints its glass without the display:none trick (see the observer
     above), so on it the scan's result is discarded — and reading the computed style
     of every element on a 32k-node page is the most expensive thing this file does. */
  if (productHero) return;
  glassEls = [...document.querySelectorAll('*')].filter((el) => {
    const cs = getComputedStyle(el);
    return (cs.backdropFilter && cs.backdropFilter !== 'none')
        || (cs.webkitBackdropFilter && cs.webkitBackdropFilter !== 'none');
  });
  glassEls.forEach(el => glassIO.observe(el));
}, 300));
function markGlassOffscreen() {
  for (const el of glassEls) {
    if (glassSeen.get(el) === false) el.dataset.repaint = '1';
  }
}

/* ---------- hero canopy videos ------------------------------------
   Every colorway ships a Seedance-animated version of its original
   expanded plate (D-chain cleanup: nlmeans + deband, 2026-07-12). Each
   art-layer carries its OWN video, so a theme switch crossfades plate,
   cutout, and animation as one entity — the same single-entity fix the
   procedural canopy got. The incoming layer's clip starts playing while
   that layer is still invisible, so the fade reveals live motion, not a
   still that pops later. The retired procedural canopy and stucco layers
   remain disabled site-wide. */
const LEAF_ENABLED = true;
/* Two crops of one film. Desktop plays the whole 1920x1080 clip over the whole plate;
   a phone plays video x 1578..1920, which is plate x 1153..1400 — the part of the film
   inside the portrait window the phone's plate is cut from. Same shadows, same places,
   160KB instead of 4MB. Shopify Files supplies content-addressed video URLs for both
   crops; the lookup module keeps those generated URLs paired with their colorways. */
const leafSrc = (id) => (HERO_DESKTOP.matches ? HERO_DESKTOP_VIDEO[id] : HERO_MOBILE_VIDEO[id]);
/* the film's own window inside the clip, in VIDEO px: where it starts and how wide */
const leafWin = () => (HERO_DESKTOP.matches ? { x: 0, w: 1920 } : { x: 1578, w: 342 });
const LEAF_FRAC = { w: 1415 / 3000, h: 796 / 1536 };
/* Seedance reframed every generation identically: the clip's content is
   the reference crop scaled 1.384× shifted (-18, -11) video px —
   measured by cross-correlating all 13 mean frames against the native
   crops (±1 px agreement, 2026-07-12). layoutLeaf undoes it so the
   counter line registers with the still. */
const LEAF_CAL = { s: 1.384, ox: -18, oy: -11 };

/* ── where the PLATE is, which is not where the <img> is ───────────────
   The film is placed in plate coordinates — that is what LEAF_CAL is expressed in — so
   everything below needs the box the full 3000x1536 plate would occupy if it were all
   drawn. On desktop that is the image's own box and this returns it unchanged. On a
   phone it is neither: the picture is a 1024-wide WINDOW at x=1153 of the plate, and it
   is drawn object-fit:contain, so it is also letterboxed inside its element. Both
   corrections happen here rather than in the placement below, which is why the placement
   below is still the one desktop was calibrated with. */
const PHONE_WIN = { x: 1153 / 3000, w: 1024 / 3000 };
function plateBox(img, r) {
  if (HERO_DESKTOP.matches) return { left: r.left, top: r.top, w: r.width, h: r.height };
  const nw = img.naturalWidth, nh = img.naturalHeight;
  if (!nw || !nh) return null;
  /* contain + object-position:50% 0 in a box narrower than the picture's aspect: it
     fills the width and is top-anchored, so the drawn height follows from the width */
  const dw = r.width, dh = dw * nh / nw;
  return { left: r.left - PHONE_WIN.x / PHONE_WIN.w * dw, top: r.top,
           w: dw / PHONE_WIN.w, h: dh };
}

function layoutLeaf(layer) {
  if (heroController) return;
  const v = layer.leaf;
  if (!v || getComputedStyle(v).display === 'none') return;
  const host = document.querySelector('.hero-art');
  if (!layer.img || !host) return;
  const hr = host.getBoundingClientRect();
  const r = layer.img.getBoundingClientRect();
  if (!r.width) return;
  const pb = plateBox(layer.img, r);
  if (!pb) return;
  const pxw = pb.w * LEAF_FRAC.w / 1415;       /* displayed size of one plate px */
  const pxh = pb.h * LEAF_FRAC.h / 796;
  /* video px v sits at plate px (v - ox) / s; the clip may start partway in */
  const win = leafWin();
  v.style.left = (pb.left - hr.left + (win.x - LEAF_CAL.ox) / LEAF_CAL.s * pxw) + 'px';
  v.style.top = (pb.top - hr.top - LEAF_CAL.oy / LEAF_CAL.s * pxh) + 'px';
  v.style.width = (win.w / LEAF_CAL.s * pxw) + 'px';
  v.style.height = (1080 / LEAF_CAL.s * pxh) + 'px';
}

function setLeaf(layer, t) {
  if (heroController || REDUCED_MOTION.matches) return;
  const v = layer.leaf;
  if (!v || !LEAF_ENABLED || getComputedStyle(v).display === 'none') return;
  const want = leafSrc(t.id);
  if (!want) {                      /* static colorways have no leaf clip */
    delete v.dataset.want;
    v.style.opacity = '0';
    v.removeAttribute('src');
    return;
  }
  if (v.dataset.want !== want) {
    v.dataset.want = want;
    v.style.opacity = '0';          /* transparent until it has frames */
    v.src = want;
  }
  layoutLeaf(layer);
  leafPlayback();
}

/* playback policy: the FRONT layer's clip runs while the hero is
   visible; the back layer's clip runs only while a crossfade is being
   prepared or finishing (state.leafWarm), then pauses */
function leafPlayback() {
  if (heroController) return;
  const visible = !REDUCED_MOTION.matches && heroInView && document.visibilityState === 'visible';
  els.layers.forEach((L, i) => {
    const v = L.leaf;
    if (!v || !v.getAttribute('src')) return;
    if (visible && (i === state.front || state.leafWarm)) v.play().catch(() => {});
    else v.pause();
  });
}

for (const L of els.layers) {
  L.leaf?.addEventListener('playing', () => { L.leaf.style.opacity = '1'; });
}
addEventListener('resize', () => { els.layers.forEach(layoutLeaf); });
HERO_DESKTOP.addEventListener('change', () => {
  if (heroController) return;
  /* the clip itself changes across the breakpoint now, not only its placement */
  for (const L of els.layers) if (L.leaf) delete L.leaf.dataset.want;
  for (const layer of els.layers) {
    const t = THEMES.find((theme) => theme.id === layer.pic?.dataset.theme);
    if (!t) continue;
    if (HERO_DESKTOP.matches) {
      if (layer.brush && BRUSH_IDS.has(t.id)) layer.brush.src = brushURL(t.id);
      setLeaf(layer, t);
    } else {
      layer.brush?.removeAttribute('src');
      if (layer.leaf) {
        layer.leaf.pause();
        layer.leaf.removeAttribute('src');
        layer.leaf.load();
        layer.leaf.style.opacity = '0';
        delete layer.leaf.dataset.want;
      }
      setLeaf(layer, t);
    }
  }
});
document.addEventListener('visibilitychange', () => { leafPlayback(); });

/* the copy scrim's tint crossfades exactly like an art layer: two
   stacked gradients whose opacities flip in the same frame with the
   same transition spec as the plate entities */
function scrimTo(t, { snap = false } = {}) {
  const front = state.scrimFront || 'a';
  const back = front === 'a' ? 'b' : 'a';
  const els2 = [document.querySelector('.copy-scrim'), document.querySelector('.feature-card')].filter(Boolean);
  root.style.setProperty(`--scrim-${back}`, t.bg2);
  /* the features bar rides the same flip — its glass fill per theme */
  root.style.setProperty(`--card-${back}`, t.mode === 'dark' ? 'rgba(255,255,255,.07)' : t.surface);
  /* dark feature-video twins crossfade on the same flip */
  root.style.setProperty('--featdark-op', t.mode === 'dark' ? '1' : '0');
  if (snap) { els2.forEach(e => e.classList.add('no-fade')); els2.forEach(e => void e.offsetWidth); }
  for (const p of ['scrim', 'card']) {
    root.style.setProperty(`--${p}-${back}-op`, '1');
    root.style.setProperty(`--${p}-${front}-op`, '0');
  }
  if (snap) { els2.forEach(e => void e.offsetWidth); els2.forEach(e => e.classList.remove('no-fade')); }
  state.scrimFront = back;
}

/* Requested selection can advance while assets load; visible selection and
   Shopify identity change together only after the latest appearance is ready. */
let themeApplied = false;

/* ── one clock, one gate ─────────────────────────────────────────────
   A colourway change should move as one: the hero, the docking clip and every
   theme-following photograph start together and end together. Two pieces:

   1. Before the hero poster is awaited, 'tao:theme-prepare' asks the rest of the
      page (pdp.js) to register whatever it needs for this colourway through
      detail.waitUntil(promise) — the photographs within a screen of the viewport;
      the rest warm alongside and swap as they land. The commit waits for the hero
      AND those, capped at PREPARE_CAP_MS so a cold, distant pick can never stall —
      after the cap the photographs simply arrive on their own, as they always did.
   2. From the moment the variables are written, html.theme-changing is held for the
      length of one --theme-fade (+80 ms of grace). A scoped rule in pdp.css puts every
      colour-bearing property on that clock with no delay while the class is present,
      so nothing snaps ahead or lags. A commit that lands mid-fade extends the hold
      rather than cutting the running fade short. */
/* The cap knows what kind of screen it is bounding, decided once at module init (the
   breakpoint does not move under a tap). A desktop shows several of the photographs
   at once and keeps 450 ms. Below the 920 px breakpoint (HERO_DESKTOP — the same
   split pdp.js uses for the picture cuts) the page has the hero and at most one
   picture on screen when a swatch is tapped, and pdp.js registers
   only those for the wait; everything else is screens below and cannot be seen
   arriving late, so 250 ms is all the time that buys anything. Measured cold on an
   iPhone, the old wait covered every picture on the page and ran the tap out to
   560–690 ms before the colours moved; desktop with warm caches commits in ~140 ms.
   The hero poster is still always awaited — commit() throws without it. */
const PREPARE_CAP_MS = HERO_DESKTOP.matches ? 450 : 250;
let themeChangeTimer = null;
function parseDurationMs(value) {
    const text = String(value || '').trim();
    const number = parseFloat(text);
    if (!Number.isFinite(number)) return 0;
    return text.endsWith('ms') ? number : number * 1000;
}
/* The fade length is read BEFORE the class goes on: a computed-style read after
   the class add would force a style pass over the whole page (32k nodes) on its
   own, ahead of the one the palette write causes anyway. Cached after the first
   read; the clock does not change at runtime. */
let themeFadeMs = 0;
/* Both clocks — the theme fade and the rings' fade-out — are read once, off the
   critical path: at the first idle moment after load, with a clean style tree,
   rather than inside the first commit (measured there on a phone: 91 ms, a
   re-style of the page while the hero was mid-change). The reads inside the
   commit remain as fallbacks for a switch that lands before that moment. */
{
    const readClocks = () => {
        if (!themeFadeMs) {
            themeFadeMs = parseDurationMs(getComputedStyle(root).getPropertyValue('--theme-fade')) || 700;
        }
        if (!state.waveOutMs) {
            const rings = document.querySelector('.sonic-stage .rings');
            state.waveOutMs = (rings
                && parseDurationMs(getComputedStyle(rings).transitionDuration.split(',')[0])) || 300;
        }
    };
    const whenIdle = window.requestIdleCallback || ((fn) => setTimeout(fn, 400));
    if (document.readyState === 'complete') whenIdle(readClocks);
    else addEventListener('load', () => whenIdle(readClocks), { once: true });
}
function holdThemeChange() {
    clearTimeout(themeChangeTimer);
    if (!themeFadeMs) themeFadeMs = parseDurationMs(getComputedStyle(root).getPropertyValue('--theme-fade')) || 700;
    root.classList.add('theme-changing');
    themeChangeTimer = setTimeout(() => {
        themeChangeTimer = null;
        root.classList.remove('theme-changing');
    }, themeFadeMs + 80);
}

function prepareLegacyLayer(layer) {
    const waitForImage = (img, isRequired) => new Promise((resolve, reject) => {
        if (!img || !img.getAttribute('src')) {
            if (isRequired) reject(new Error('The selected hero has no fallback image.'));
            else resolve();
            return;
        }
        let settled = false;
        const finish = error => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            img.removeEventListener('load', handleLoad);
            img.removeEventListener('error', handleError);
            if (error) reject(error);
            else resolve();
        };
        const handleError = () => finish(new Error('The selected hero image could not load.'));
        const handleLoad = () => {
            if (!img.naturalWidth) return handleError();
            if (img.decode) img.decode().then(() => finish(), handleError);
            else finish();
        };
        /* A failed asset never retires the last valid picture or changes the
           purchasing variant. This timeout releases the request, not the image. */
        const timeout = setTimeout(handleError, 15000);
        img.addEventListener('load', handleLoad);
        img.addEventListener('error', handleError);
        if (img.complete) handleLoad();
    });
    return Promise.all([waitForImage(layer?.img, true), waitForImage(layer?.brush, false)]);
}

function applyTheme(id, { instant = false, notifySelection = true, restore = false } = {}) {
    const t = THEMES.find(theme => theme.id === id);
    if (!t) return Promise.resolve(false);
    if (!restore && !instant && state.requestedThemeId === t.id) {
        if (state.selectionPending) return state.selectionPromise;
        if (themeApplied && state.themeId === t.id) return Promise.resolve(true);
    }

    const generation = ++state.themeGen;
    state.requestedThemeId = t.id;
    state.selectionPending = true;
    const shouldSnap = instant || restore || REDUCED_MOTION.matches || !heroInView;
    const front = els.layers[state.front];
    const back = els.layers[1 - state.front];
    const incoming = themeApplied ? back : front;
    const stage = document.querySelector('.sonic-stage');
    /* variant art sits inside aria-hidden hero art: decorative, the h1 names the product */
    const alt = USES_VARIANT_ART ? '' : `TAO Clean sonic toothbrush and Aura Clean Station in ${t.name}`;
    const map = {
        '--bg-2': t.bg2,
        '--ink': t.ink, '--muted': t.muted,
        '--accent': t.accent, '--accent-ink': t.accentInk, '--accent-strong': t.accentStrong,
        '--surface': t.surface, '--surface-strong': t.surfaceStrong,
        '--hairline': t.hairline,
        ...(t.hero ? (() => {
            const height = 79.7 / (t.hero.bottom - t.hero.top);
            return {
                '--hero-h': height.toFixed(1) + '%',
                '--hero-top': (91.4 - t.hero.bottom * height).toFixed(1) + '%',
                '--hero-cx': (-t.hero.cx * 100).toFixed(1) + '%',
            };
        })() : {}),
        '--shadow': t.mode === 'dark'
            ? '0 24px 70px rgba(0,0,0,.55)'
            : '0 24px 60px rgba(30,40,60,.14)',
    };
    const ringMap = {
        '--ring-opacity': String(t.ringOpacity ?? .45),
        '--ring-color': t.ring ?? t.accentStrong,
        '--ring-color-2': t.ring2 ?? t.ring ?? t.accentStrong,
    };
    /* Written where they are read, not on :root: the rings live in the sonic
       stage, the spotlight behind the hero art (.hero-art::after) in the hero.
       A custom property set on :root re-resolves the style of every element on
       the page (~280 ms on a phone for this DOM), which this did a second time,
       mid-fade, when the waves came back; these two subtrees are ~200 elements. */
    const ringHosts = [document.querySelector('.hero'), stage].filter(Boolean);
    if (!ringHosts.length) ringHosts.push(root);
    const applyRingVars = () => {
        for (const host of ringHosts) {
            for (const [key, value] of Object.entries(ringMap)) host.style.setProperty(key, value);
        }
    };
    const applyVars = () => {
        document.body.dataset.mode = t.mode;
        document.body.dataset.taoTheme = t.id;
        for (const [key, value] of Object.entries(map)) root.style.setProperty(key, value);
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', t.bg2);
        dispatchEvent(new CustomEvent('tao:theme', { detail: t }));
    };
    const commit = () => {
        if (generation !== state.themeGen) return false;
        const wasApplied = themeApplied;
        const shouldNotify = notifySelection && (!wasApplied || state.themeId !== t.id);
        if (heroController) {
            heroController.commit(t.id, { snap: shouldSnap });
        } else {
            if (!incoming?.pic) return false;
            if (shouldSnap) {
                incoming.pic.style.transition = 'none';
                if (front?.pic) front.pic.style.transition = 'none';
            }
            incoming.pic.style.opacity = '1';
            if (incoming !== front && front?.pic) front.pic.style.opacity = '0';
            state.front = els.layers.indexOf(incoming);
            if (shouldSnap) {
                void incoming.pic.offsetWidth;
                incoming.pic.style.transition = '';
                if (front?.pic) front.pic.style.transition = '';
            }
        }

        state.themeId = t.id;
        state.requestedThemeId = t.id;
        state.selectionPending = false;
        themeApplied = true;
        if (wasApplied) browsingColours = true;
        try {
            localStorage.setItem('tao-theme', t.id);
            localStorage.setItem('tao-boot-vars', JSON.stringify({
                mode: t.mode, vars: { ...map, ...ringMap },
            }));
        } catch (error) { /* Persistence is optional, including in private browsing. */ }

        /* No promise, frame or image callback may split this transaction. The
           native buy adapter receives the same selection as the visible frame. */
        if (shouldNotify) {
            document.dispatchEvent(new CustomEvent('tao:selection', { detail: { themeId: t.id } }));
        }
        for (const button of els.swatches?.children || []) {
            const isSelected = button.dataset.id === t.id;
            button.setAttribute('aria-selected', String(isSelected));
            button.tabIndex = isSelected ? 0 : -1;
        }
        if (els.name) els.name.textContent = t.name;
        /* instant (boot, the lab toggle) and restore (a synchronous commerce rollback)
           are the two callers that explicitly want no fade at all */
        if (!instant && !restore) holdThemeChange();
        /* Fallback for a switch before the idle read above: the wave fade-out's
           length is read before the palette is written — a getComputedStyle after
           it would force the whole page's re-style at the worst moment. */
        if (!shouldSnap && !state.waveOutMs) {
            const rings = stage?.querySelector('.rings');
            state.waveOutMs = (rings
                && parseDurationMs(getComputedStyle(rings).transitionDuration.split(',')[0])) || 300;
        }
        applyVars();
        markGlassOffscreen();
        scrimTo(t, { snap: shouldSnap });
        if (!heroController) els.layers.forEach(layoutLeaf);
        clearTimeout(state.waveTimer);
        /* The sonic rings keep their own two-step recolour, deliberately. Their stroke
           is set INSIDE the `ripple` keyframes (site.css) as a color-mix of --ring-color,
           and a running animation beats any transition in the cascade, so a variable
           swap under it snaps to the new palette on the next frame — no fade can be
           applied to it. The waves therefore fade out on the old palette, take the new
           variables while invisible, and fade back in: starting on the commit and, at
           site.css's .3s, finishing at ~640 ms, inside the 700 ms theme fade. The
           fade-out's length is read rather than assumed (the theme-changing override
           can put .rings on the theme clock), so the swap never lands mid-fade. */
        if (shouldSnap) {
            applyRingVars();
            stage?.classList.remove('wave-out');
        } else {
            stage?.classList.add('wave-out');
            const waveOut = state.waveOutMs || 300;
            state.waveTimer = setTimeout(() => {
                applyRingVars();
                stage?.classList.remove('wave-out');
            }, waveOut + 40);
        }
        if (!heroController) {
            clearTimeout(state.leafTimer);
            state.leafWarm = !shouldSnap;
            leafPlayback();
            state.leafTimer = setTimeout(() => {
                state.leafWarm = false;
                leafPlayback();
            }, shouldSnap ? 0 : 760);
            const index = THEMES.findIndex(theme => theme.id === t.id);
            preload(THEMES[(index + 1) % THEMES.length]);
            preload(THEMES[(index - 1 + THEMES.length) % THEMES.length]);
        }
        root.classList.add('hero-ready');
        return true;
    };
    const handleFailure = error => {
        if (generation !== state.themeGen) return false;
        state.requestedThemeId = state.themeId;
        state.selectionPending = false;
        state.leafWarm = false;
        leafPlayback();
        stopPlay();
        console.warn('TAO hero selection could not be prepared.', error);
        return false;
    };

    try {
        if (!heroController) {
            if (themeApplied && incoming?.pic) {
                incoming.pic.style.transition = 'none';
                incoming.pic.style.opacity = '0';
                void incoming.pic.offsetWidth;
                incoming.pic.style.transition = '';
            }
            setLayer(incoming, t, alt);
            state.leafWarm = true;
            leafPlayback();
        }
        /* The controller retains its previous committed appearance. Commerce
           failure rollback must restore it synchronously before the next paint. */
        if (restore) {
            state.selectionPromise = Promise.resolve(commit());
        } else {
            const preparation = heroController
                ? heroController.prepare(t.id)
                : prepareLegacyLayer(incoming);
            /* The poster request is queued first (it is the critical path), then the
               page registers its own preparations for the same colourway. An instant
               apply still announces the colourway but does not wait on anyone. */
            const waits = [];
            dispatchEvent(new CustomEvent('tao:theme-prepare', { detail: {
                id: t.id,
                waitUntil(promise) { waits.push(Promise.resolve(promise).catch(() => {})); },
            } }));
            /* The hero itself is always awaited — commit() needs its decoded poster —
               so the cap bounds only the page's waits. The generation check inside
               commit() still makes sure only the latest request lands. */
            const gate = !instant && waits.length ? Promise.race([
                Promise.all([preparation, ...waits]),
                new Promise(resolve => setTimeout(resolve, PREPARE_CAP_MS)),
            ]).then(() => preparation) : preparation;
            state.selectionPromise = Promise.resolve(gate).then(commit).catch(handleFailure);
        }
    } catch (error) {
        state.selectionPromise = Promise.resolve(handleFailure(error));
    }
    return state.selectionPromise;
}

/* ---------- picker ---------------------------------------------- */
const swatchLum = (hex) => {
  const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16) / 255);
  return .2126 * r + .7152 * g + .0722 * b;
};
for (const t of THEMES) {
  const b = els.swatches.querySelector(`[data-id="${t.id}"]`) || document.createElement('button');
  b.className = 'swatch';
  b.type = 'button';
  b.role = 'option';
  b.dataset.id = t.id;
  b.dataset.action = 'umma:select-color';
  b.tabIndex = t.id === state.themeId ? 0 : -1;
  b.setAttribute('aria-label', t.name);
  b.setAttribute('aria-selected', String(t.id === state.themeId));
  b.style.setProperty('--sw', t.swatch);
  /* selection ring adapts to the swatch's own brightness */
  const light = swatchLum(t.swatch) > .6;
  b.style.setProperty('--ring-ui', light ? 'rgba(20,22,26,.62)' : '#fff');
  b.style.setProperty('--ring-ui-edge', light ? 'rgba(255,255,255,.55)' : 'rgba(0,0,0,.30)');
  if (!b.parentElement) els.swatches.appendChild(b);
}

function step(dir) {
  const i = THEMES.findIndex(x => x.id === (state.requestedThemeId || state.themeId));
  return applyTheme(THEMES[(i + dir + THEMES.length) % THEMES.length].id);
}

function handleSelectColor(button) {
    stopPlay();
    applyTheme(button.dataset.id);
}

function handlePreviousColor() {
    stopPlay();
    step(-1);
}

function handleNextColor() {
    stopPlay();
    step(1);
}

function handleToggleCycle() {
    if (state.playing) stopPlay();
    else startPlay();
}

const heroClickHandlers = new Map([
    ['[data-action="umma:select-color"]', handleSelectColor],
    ['[data-action="umma:previous-color"]', handlePreviousColor],
    ['[data-action="umma:next-color"]', handleNextColor],
    ['[data-action="umma:toggle-cycle"]', handleToggleCycle],
]);

function handleHeroClick(event) {
    if (!(event.target instanceof Element)) return;
    for (const [selector, handler] of heroClickHandlers) {
        const button = event.target.closest(selector);
        if (!button) continue;
        handler(button);
        break;
    }
}

document.addEventListener('click', handleHeroClick);

/* ── paging the hero: arrows and a swipe ───────────────────────────────
   The swatch row picks a colour; this pages through them. Both call the same step()
   the arrow keys already used, so there is one way a colourway changes.

   The swipe is axis-locked the way the homepage carousels are: a horizontal listener
   that cannot refuse the page's vertical scroll is worse than no listener, because the
   hero is the first thing under a thumb that is trying to scroll down. Nothing is
   decided until the gesture has committed to an axis, and a touch that starts on a
   control — a swatch, the cart button, the play toggle — is that control's, not ours.
   So is one that starts on a hero's own sideways scroller (the travel case gallery,
   [data-tc-rail]): cancelling its moves would stop the swipe. */
{
  const hero = document.querySelector('.hero');
  const page = (dir) => { stopPlay(); step(dir); };
  const previous = document.querySelector('.hero-prev');
  const next = document.querySelector('.hero-next');
  if (previous) previous.dataset.action = 'umma:previous-color';
  if (next) next.dataset.action = 'umma:next-color';

  if (hero) {
    let x0 = null, y0 = null, lock = null;
    const onControl = (t) => !!(t.closest && t.closest('button, a, input, .swatches, .picker, [data-tc-rail]'));
    hero.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1 || onControl(e.target)) { x0 = null; return; }
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; lock = null;
    }, { passive: true });
    hero.addEventListener('touchmove', (e) => {
      if (x0 == null) return;
      const dx = e.touches[0].clientX - x0, dy = e.touches[0].clientY - y0;
      if (lock === null) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        lock = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
      }
      if (lock === 'x' && e.cancelable) e.preventDefault();   /* ours now, so the page must not also scroll */
    }, { passive: false });
    hero.addEventListener('touchend', (e) => {
      if (x0 == null || lock !== 'x') { x0 = null; return; }
      const dx = e.changedTouches[0].clientX - x0;
      x0 = null; lock = null;
      if (Math.abs(dx) >= 45) page(dx < 0 ? 1 : -1);          /* drag left = next */
    }, { passive: true });

    /* Mouse paging belongs to the photograph. Copy remains ordinary selectable
       text, and cancelled or vertical drags must not become color selections. */
    let mouseStart = null;
    function resetHeroMouseGesture() {
        mouseStart = null;
    }
    function handleHeroPointerDown(event) {
        resetHeroMouseGesture();
        if (event.pointerType !== 'mouse' || event.button !== 0 || onControl(event.target)
            || event.target.closest('.hero-copy')) return;
        mouseStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
    }
    function handleHeroPointerUp(event) {
        const start = mouseStart;
        resetHeroMouseGesture();
        if (!start || event.pointerType !== 'mouse' || event.pointerId !== start.id
            || onControl(event.target) || event.target.closest('.hero-copy')
            || window.getSelection()?.isCollapsed === false) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) >= 60 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            page(dx < 0 ? 1 : -1);
        }
    }
    hero.addEventListener('pointerdown', handleHeroPointerDown);
    hero.addEventListener('pointerup', handleHeroPointerUp);
    hero.addEventListener('pointercancel', resetHeroMouseGesture);
    hero.addEventListener('pointerleave', resetHeroMouseGesture);
  }
}

function handleHeroKeydown(e) {
  if (e.defaultPrevented) return;         /* a widget (e.g. the concern tabs) claimed this key */
  if (!(e.target instanceof Element)) return;
  if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
  e.preventDefault();
  const shouldMoveFocus = els.swatches?.contains(document.activeElement);
  stopPlay();
  step(e.key === 'ArrowLeft' ? -1 : 1).then(committed => {
      if (committed && shouldMoveFocus) {
          els.swatches.querySelector('[aria-selected="true"]')?.focus({ preventScroll: true });
      }
  });
}

document.addEventListener('keydown', handleHeroKeydown);

/* auto-cycle */
function startPlay() {
  if (state.playing || REDUCED_MOTION.matches || THEMES.length < 2) return;
  state.playing = true;
  els.play.setAttribute('aria-pressed', 'true');
  els.play.querySelector('use').setAttribute('href', '#ic-pause');
  els.play.querySelector('.pp-label').textContent = 'Pause';
  state.timer = setInterval(() => {
    /* Keep the play choice, but do not recolor and repaint an entire long page
       while its color-cycle control is offscreen. Resume naturally on return. */
    if (heroInView && !document.hidden) step(1);
  }, 3200);
}
function stopPlay() {
  if (!state.playing) return;
  state.playing = false;
  els.play.setAttribute('aria-pressed', 'false');
  els.play.querySelector('use').setAttribute('href', '#ic-play');
  els.play.querySelector('.pp-label').textContent = 'Cycle';
  clearInterval(state.timer);
}
els.play.dataset.action = 'umma:toggle-cycle';

/* ---------- about video ---------------------------------------- */
let updateAmbLoop = () => {};
const vid = $('#about-video');
const vidFrame = document.querySelector('.video-frame');
/* The founder film block only exists on the toothbrush PDP. Other mockup PDPs
   (the water flosser) share this module without it, so everything that reads
   #about-video / .video-frame / .ambilight lives inside this guard. */
if (vid && vidFrame) {

/* video streams full-res from R2; if that ever fails, drop to the
   bundled copy (same-origin, so no crossorigin needed) */
vid.addEventListener('error', () => {
  const fb = vid.dataset.fallback;
  if (!fb || vid.dataset.usedFallback) return;
  vid.dataset.usedFallback = '1';
  const t = vid.currentTime || 0, wasPlaying = !vid.paused;
  vid.removeAttribute('crossorigin');
  vid.src = fb;
  vid.load();
  vid.addEventListener('loadedmetadata', () => {
    try { vid.currentTime = t; } catch (e) {}
    if (wasPlaying) vid.play();
  }, { once: true });
});

/* teaser: rotate through hand-picked frames of the film */
/* hand-picked frame order (dropped 14/15/16/17/20/22; 25 moved before 23) */
const PREVIEW_ORDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 18, 19, 21, 25, 23, 24, 26, 27, 28, 29, 30, 31, 32];
const PREVIEWS = PREVIEW_ORDER.map(n => `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--video--preview--preview-${n}.jpg?v=5`);
/* vc.done: the teaser plays the frames through ONCE and settles on the last one. It
   used to cycle for as long as the section was on screen, which meant fetching and
   decoding a fresh JPEG every three seconds forever; one pass shows every frame. */
const vc = { a: document.querySelector('.vc-a'), b: document.querySelector('.vc-b'), i: 0, front: 'a', timer: null, loading: false, done: false };
const previewWarm = new Map();
let aboutInView = false;
let previewStarted = false;
function warmPreview(i) {
  const src = PREVIEWS[i % PREVIEWS.length];
  if (previewWarm.has(src)) return;
  const im = new Image();
  im.src = src;
  previewWarm.set(src, im);
}
function ensurePreview() {
  if (previewStarted) return;
  previewStarted = true;
  vc.a.src = PREVIEWS[0];
  vc.a.classList.add('on');
  warmPreview(1);
}
function cycleNext() {
  if (vc.loading || vc.done) return;
  ensurePreview();
  const next = (vc.i + 1) % PREVIEWS.length;
  const showing = vc.front === 'a' ? vc.a : vc.b;
  const hidden  = vc.front === 'a' ? vc.b : vc.a;
  vc.loading = true;
  let complete = false;
  const reveal = () => {
    if (complete) return;
    complete = true;
    vc.loading = false;
    if (!aboutInView || vidFrame.classList.contains('playing')) return;
    vc.i = next;
    hidden.classList.add('on');
    showing.classList.remove('on');
    vc.front = vc.front === 'a' ? 'b' : 'a';
    if (next + 1 < PREVIEWS.length) warmPreview(next + 1);
    else { vc.done = true; stopCycle(); }   /* one full pass: the last frame stays */
  };
  const fail = () => { if (!complete) { complete = true; vc.loading = false; } };
  hidden.onload = reveal;
  hidden.onerror = fail;
  hidden.src = PREVIEWS[next];
  if (hidden.complete && hidden.naturalWidth) reveal();
}
function startCycle() {
  if (vc.timer || vc.done || !aboutInView || document.hidden || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  ensurePreview();
  vc.timer = setInterval(cycleNext, 3000);
}
function stopCycle() { clearInterval(vc.timer); vc.timer = null; }
new IntersectionObserver((entries) => {
  aboutInView = entries.some((entry) => entry.isIntersecting);
  if (aboutInView && !vidFrame.classList.contains('playing')) startCycle();
  else stopCycle();
  updateAmbLoop();
}, { rootMargin: '220px 0px', threshold: 0 }).observe(vidFrame);
document.addEventListener('visibilitychange', () => {
  if (document.hidden) stopCycle();
  else if (aboutInView && !vidFrame.classList.contains('playing')) startCycle();
  updateAmbLoop();
});

/* ambilight: paint the current frame, downscaled, into the blurred canvas */
const amb = document.querySelector('.ambilight');
const ambx = amb.getContext('2d');
let ambLast = 0;
/* size the (tiny, blurred) backing buffer to the source aspect */
function ambSize(w, h) {
  const bw = 160, bh = Math.max(1, Math.round(bw * h / w));
  if (amb.width !== bw || amb.height !== bh) { amb.width = bw; amb.height = bh; }
}
function ambDraw(src) {
  const w = src.videoWidth || src.naturalWidth, h = src.videoHeight || src.naturalHeight;
  if (!w || !h) return;
  ambSize(w, h);
  ambx.globalAlpha = 1;
  try { ambx.drawImage(src, 0, 0, amb.width, amb.height); } catch (e) {}
}
/* The loop exists only while it can be seen: it runs while the film section is in
   view and the tab is visible, and is cancelled otherwise (it used to tick at every
   frame for the life of the page, returning early). Reduced motion skips it: the
   halo would only ever mirror a still. So does a hidden canvas: below 920px
   site.css sets it display: none (a static glow on the frame stands in for the
   64px blur), and a hidden canvas has no offsetParent, so the loop is never
   started rather than ticking to its early return. HERO_DESKTOP flips at the same
   breakpoint and re-evaluates it when a tablet rotates. */
let ambFrame = 0;
function ambLoop(t) {
  ambFrame = 0;
  if (!aboutInView || document.hidden || REDUCED_MOTION.matches) return;
  ambFrame = requestAnimationFrame(ambLoop);
  if (t - ambLast < 33) return;
  ambLast = t;
  if (!amb.offsetParent) return;

  /* video layer visible (playing OR paused mid-video, keyed off the
     `.playing` class not vid.paused): mirror its live frame */
  if (vidFrame.classList.contains('playing') && vid.readyState >= 2 && vid.videoWidth) {
    ambDraw(vid);
    return;
  }

  /* preview slideshow: reproduce the DOM compositing exactly — the two
     <img> layers over the black frame background, each at its live CSS
     opacity — so the halo fades between frames in perfect step with the
     pictures AND dims through the crossfade just as they do, instead of
     staying lit and flaring past them (the flash). Because opacity_a +
     opacity_b == 1 at every instant, the midpoint shows ~25% black —
     matching the frames rather than over-brightening. */
  const dim = vc.a.naturalWidth ? vc.a : (vc.b.naturalWidth ? vc.b : null);
  if (!dim) { if (vid.readyState >= 2 && vid.videoWidth) ambDraw(vid); return; }
  ambSize(dim.naturalWidth, dim.naturalHeight);
  ambx.globalAlpha = 1;
  ambx.fillStyle = '#000';
  ambx.fillRect(0, 0, amb.width, amb.height);
  for (const img of [vc.a, vc.b]) {              // DOM paint order: a under b
    if (!img.complete || !img.naturalWidth) continue;
    ambx.globalAlpha = Math.min(1, Math.max(0, parseFloat(getComputedStyle(img).opacity) || 0));
    try { ambx.drawImage(img, 0, 0, amb.width, amb.height); } catch (e) {}
  }
  ambx.globalAlpha = 1;
}
updateAmbLoop = function () {
  const shouldRun = aboutInView && !document.hidden && !REDUCED_MOTION.matches && !!amb.offsetParent;
  if (shouldRun && !ambFrame) ambFrame = requestAnimationFrame(ambLoop);
  else if (!shouldRun && ambFrame) { cancelAnimationFrame(ambFrame); ambFrame = 0; }
};
updateAmbLoop();
HERO_DESKTOP.addEventListener('change', updateAmbLoop);

$('#video-play').addEventListener('click', () => {
  vidFrame.classList.add('playing');
  stopCycle();
  vid.controls = true;
  vid.muted = false;
  vid.volume = 1;
  vid.play();
});
vid.addEventListener('ended', () => {
  vidFrame.classList.remove('playing');
  vid.controls = false;
  if (aboutInView) startCycle();
});

}
/* end of the film block guard */
/* ---------- boot ------------------------------------------------ */
const bootSelection = applyTheme(state.themeId, { instant: true });
/* re-enable theme transitions once the saved theme has painted */
requestAnimationFrame(() => requestAnimationFrame(() =>
  document.documentElement.classList.remove('no-fade')));

/* First-load curtain: wait briefly for the primary product imagery, but never
   gate the reveal on the optional canvas canopy. Some browsers do not paint
   that canvas until a scroll/visibility update, which made the hero appear to
   require user interaction before its entrance animation could begin. */
if (!heroController) {
  const front = els.layers[state.front];
  const waitImg = (im) => new Promise((res) => {
    if (!im || !im.getAttribute('src')) return res();
    const done = () => res();
    const dec = () => (im.decode ? im.decode().then(done, done) : done());
    if (im.complete && im.naturalWidth) dec();
    else {
      im.addEventListener('load', dec, { once: true });
      im.addEventListener('error', done, { once: true });
    }
  });
  const fontsReady = document.fonts?.ready || Promise.resolve();
  Promise.race([
    Promise.all([bootSelection, waitImg(front?.img), waitImg(front?.brush), fontsReady]),
    new Promise((res) => setTimeout(res, 900)),
  ]).then(() => requestAnimationFrame(() =>
    document.documentElement.classList.add('hero-ready')));
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) stopPlay();
});

/* Every external request uses the same readiness and atomic commit path. */
function handleRequestedTheme(event) {
    stopPlay();
    applyTheme(event.detail);
}

function handleSelectionRestore(event) {
    if (!event.detail?.themeId) return;
    stopPlay();
    applyTheme(event.detail.themeId, { instant: true, notifySelection: false, restore: true });
}

function handleMotionPreference() {
    if (REDUCED_MOTION.matches) stopPlay();
    els.play.style.display = REDUCED_MOTION.matches ? 'none' : '';
    if (!heroController && !REDUCED_MOTION.matches) {
        for (const layer of els.layers) {
            const theme = THEMES.find(candidate => candidate.id === layer.pic?.dataset.theme);
            if (theme) setLeaf(layer, theme);
        }
    }
    leafPlayback();
    updateAmbLoop();
}

function handleHeroSectionUnload(event) {
    if (!productHero || !(event.target instanceof Element) || !event.target.contains(productHero)) return;
    ++state.themeGen;
    stopPlay();
    heroController?.destroy();
}

addEventListener('tao:set-theme', handleRequestedTheme);
document.addEventListener('tao:selection-restore', handleSelectionRestore);
document.addEventListener('shopify:section:unload', handleHeroSectionUnload);
REDUCED_MOTION.addEventListener('change', handleMotionPreference);
handleMotionPreference();
